import {
  isGeomKind,
  type ShapeObjectType,
  type ShapeToolKind,
} from "@bezpart-board/shared";
import type { SceneHost, SceneObject } from "../SceneHost";
import {
  bboxFromPointerDrag,
  diamondPathD,
  geomPathD,
  starPathD,
  trianglePathD,
} from "../geometry/shapePaths";
import { geomVolumeLayers, isVolumeGeomKind } from "../geometry/volumeShapes";
import { applyShapeStyleFields } from "../render/shapes/shapePaint";
import { getShapeSettings, shapeStyleFields } from "../shapeSession";
import { applyDocumentContext, documentContextAt, ensureAnnotationAboveDocument } from "../document/documentContext";
import { applyFrameContext, frameAtPoint } from "../frame/frameContext";
import { isBoardEditorChrome, isBoardWidgetChrome } from "./boardWidgetChrome";

const SVG_NS = "http://www.w3.org/2000/svg";

export type ShapeToolBinding = {
  destroy: () => void;
};

function previewPathD(kind: ShapeToolKind, w: number, h: number): string {
  if (kind === "triangle") return trianglePathD(w, h);
  if (kind === "diamond") return diamondPathD(w, h);
  if (kind === "star") return starPathD(w, h, 5);
  if (isGeomKind(kind)) return geomPathD(kind, w, h);
  return diamondPathD(w, h);
}

function appendPreviewLayers(
  g: SVGGElement,
  kind: ShapeToolKind,
  w: number,
  h: number,
  stroke: string,
  fill: string,
  strokeWidth: number
) {
  if (isGeomKind(kind) && isVolumeGeomKind(kind)) {
    const layers = geomVolumeLayers(kind, w, h);
    if (layers) {
      const dash = `${Math.max(1, strokeWidth) * 2.2} ${Math.max(1, strokeWidth) * 2.2}`;
      for (const layer of layers) {
        const el = document.createElementNS(SVG_NS, "path");
        el.setAttribute("d", layer.d);
        el.setAttribute("stroke-linejoin", "round");
        el.setAttribute("stroke-linecap", "round");
        if (layer.paint === "fill") {
          el.setAttribute("fill", fill);
          el.setAttribute("stroke", "none");
        } else {
          el.setAttribute("fill", "none");
          el.setAttribute("stroke", stroke);
          el.setAttribute("stroke-width", String(strokeWidth));
          if (layer.paint === "dashed") el.setAttribute("stroke-dasharray", dash);
        }
        g.appendChild(el);
      }
      return;
    }
  }

  let el: SVGElement;
  if (kind === "rect") {
    el = document.createElementNS(SVG_NS, "rect");
    el.setAttribute("x", "0");
    el.setAttribute("y", "0");
    el.setAttribute("width", String(w));
    el.setAttribute("height", String(h));
  } else if (kind === "ellipse") {
    el = document.createElementNS(SVG_NS, "ellipse");
    el.setAttribute("cx", String(w / 2));
    el.setAttribute("cy", String(h / 2));
    el.setAttribute("rx", String(w / 2));
    el.setAttribute("ry", String(h / 2));
  } else {
    el = document.createElementNS(SVG_NS, "path");
    el.setAttribute("d", previewPathD(kind, w, h));
    if (kind === "ring") el.setAttribute("fill-rule", "evenodd");
  }
  el.setAttribute("fill", fill);
  el.setAttribute("stroke", stroke);
  el.setAttribute("stroke-width", String(strokeWidth));
  el.setAttribute("stroke-linejoin", "round");
  g.appendChild(el);
}

function createShapePreviewSvg(): {
  root: SVGSVGElement;
  setBbox: (
    kind: ShapeToolKind,
    x: number,
    y: number,
    w: number,
    h: number,
    stroke: string,
    fill: string,
    strokeWidth: number
  ) => void;
} {
  const root = document.createElementNS(SVG_NS, "svg");
  root.setAttribute("class", "wb-shape-preview");
  root.style.position = "absolute";
  root.style.left = "0";
  root.style.top = "0";
  root.style.width = "100%";
  root.style.height = "100%";
  root.style.overflow = "visible";
  root.style.pointerEvents = "none";

  const g = document.createElementNS(SVG_NS, "g");
  root.appendChild(g);

  return {
    root,
    setBbox(kind, x, y, w, h, stroke, fill, strokeWidth) {
      root.style.left = `${x}px`;
      root.style.top = `${y}px`;
      root.style.width = `${w}px`;
      root.style.height = `${h}px`;
      root.setAttribute("viewBox", `0 0 ${w} ${h}`);
      while (g.firstChild) g.removeChild(g.firstChild);
      appendPreviewLayers(g, kind, w, h, stroke, fill, strokeWidth);
    },
  };
}

function draftFromToolKind(
  kind: ShapeToolKind,
  bbox: { x: number; y: number; w: number; h: number },
  style: SceneObject["style"],
  showCenter?: boolean
): SceneObject {
  const base = {
    id: crypto.randomUUID(),
    x: bbox.x,
    y: bbox.y,
    w: bbox.w,
    h: bbox.h,
    style,
  };
  const center = showCenter ? { showCenter: true } : {};
  if (isGeomKind(kind)) {
    return { ...base, ...center, type: "geom" as const, kind };
  }
  const classic = kind as Exclude<ShapeObjectType, "geom">;
  const draft: SceneObject = { ...base, ...center, type: classic };
  if (classic === "star") {
    draft.starPoints = 5;
  }
  return draft;
}

export function bindShapeTool(host: SceneHost): ShapeToolBinding {
  let drawing = false;
  let start: { x: number; y: number } | null = null;
  let lastShift = false;
  let lastAlt = false;
  const preview = createShapePreviewSvg();

  function clearPreview() {
    preview.root.remove();
  }

  function updatePreview(end: { x: number; y: number }) {
    if (!start) return;
    const bbox = bboxFromPointerDrag(start, end, lastShift, lastAlt);
    if (!bbox) {
      preview.root.remove();
      return;
    }
    const shape = getShapeSettings();
    if (!preview.root.parentElement) {
      host.selectionOverlay.appendChild(preview.root);
    }
    preview.setBbox(
      shape.kind,
      bbox.x,
      bbox.y,
      bbox.w,
      bbox.h,
      shape.stroke,
      shape.fill,
      shape.strokeWidth
    );
  }

  function onPointerDown(ev: PointerEvent) {
    if (!host.canEdit || host.getActiveTool() !== "shape") return;
    if (host.isInteractionBlocked()) return;
    if (isBoardEditorChrome(ev.target) || isBoardWidgetChrome(ev.target)) return;
    if (ev.button !== 0) return;
    ev.preventDefault();

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    drawing = true;
    start = { x: pt.x, y: pt.y };
    lastShift = ev.shiftKey;
    lastAlt = ev.altKey;
    updatePreview(pt);

    document.addEventListener("pointermove", onPointerMove);
    document.addEventListener("pointerup", onPointerUp);
    document.addEventListener("pointercancel", onPointerCancel);
  }

  function onPointerMove(ev: PointerEvent) {
    if (!drawing || !start) return;
    lastShift = ev.shiftKey;
    lastAlt = ev.altKey;
    updatePreview(host.stagePoint(ev.clientX, ev.clientY));
  }

  function endDrawing(commit: boolean, ev?: PointerEvent) {
    if (!drawing) return;
    drawing = false;
    document.removeEventListener("pointermove", onPointerMove);
    document.removeEventListener("pointerup", onPointerUp);
    document.removeEventListener("pointercancel", onPointerCancel);
    clearPreview();

    const anchor = start;
    start = null;
    if (!commit || !anchor || !ev) return;

    const pt = host.stagePoint(ev.clientX, ev.clientY);
    const bbox = bboxFromPointerDrag(anchor, pt, ev.shiftKey, ev.altKey);
    if (!bbox) return;

    const shape = getShapeSettings();
    const style = applyShapeStyleFields(shapeStyleFields(shape));
    const draft = draftFromToolKind(shape.kind, bbox, style, shape.showCenter);

    applyDocumentContext(
      draft,
      documentContextAt(host.getObjects(), bbox.x + bbox.w / 2, bbox.y + bbox.h / 2)
    );
    ensureAnnotationAboveDocument(draft, host.getObjects());
    applyFrameContext(
      draft,
      frameAtPoint(host.getObjects(), bbox.x + bbox.w / 2, bbox.y + bbox.h / 2)
    );

    const label = isGeomKind(shape.kind) ? `geom-${shape.kind}` : shape.kind;
    void host.addObject(draft, `${label}-${draft.id}`).then(() => {
      host.setSelection([draft.id]);
      host.setActiveTool?.("select");
    });
  }

  function onPointerUp(ev: PointerEvent) {
    endDrawing(true, ev);
  }

  function onPointerCancel() {
    endDrawing(false);
  }

  function onViewportGestureStart() {
    endDrawing(false);
  }

  host.hitSurface.addEventListener("pointerdown", onPointerDown);
  window.addEventListener("wb:viewport-gesture-start", onViewportGestureStart);

  return {
    destroy() {
      endDrawing(false);
      host.hitSurface.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("wb:viewport-gesture-start", onViewportGestureStart);
    },
  };
}
